import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {emailEncryptionTypes} from 'common/constants';
import {useToggle} from 'common/hooks/useToggle';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import FormikDropdown from 'components/inputs/FormikDropdown';
import FormikNumberField from 'components/inputs/FormikNumberField';
import FormikTextField from 'components/inputs/FormikTextField';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import {Offset} from 'components/Offset';
import Panel from 'components/Panel';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {EmailSettings, emailSettingsSchema} from 'types/EmailSettings';
import {DialogTypes} from 'common/names';
import {useSendData} from 'features/layout/CommonPage/redux/hooks/useSendData';
import Dialog from 'components/Dialog';
import {useEmailSettings} from 'redux/settings/hooks/useEmailSettings';
import {TestIncomingEmailTransport} from 'features/layout/CommonPage/panels/TransportPanel/components/TestIncomingEmailTransport';
import {TestOutgoingEmailTransport} from 'features/layout/CommonPage/panels/TransportPanel/components/TestOutgoingEmailTransport';
import AsyncContainer from 'components/AsyncContainer';
import {isEmpty} from 'lodash';
import {IncomingEmailType} from 'types/EmailServerSettings';
import {castToSend} from 'common/yup/castToSend';
import FormikPassphrase from 'components/inputs/FormikPassphrase';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';
import {useFormContext} from 'react-hook-form';

type FormProps = {
   ok: (values: EmailSettings, callbacks: FormOnSubmitHelpers<any>) => any;
   cancel: () => void;
};

const SettingsEmailForm = ({cancel}: {cancel: FormProps['cancel']}) => {
   const {t} = useTranslation();
   const [showInboundServerPassword] = useToggle();
   const [showOutboundServerPassword] = useToggle();
   const getValue = useFieldValueGetter<EmailSettings>();
   const authentication = getValue('outgoingServer.authentication');
   const timestamp = getValue('timestamp');
   const lastUser = getValue('lastUser');

   const usernameIncoming = getValue('incomingServer.username');
   const passwordIncoming = getValue('incomingServer.password');
   const addressIncoming = getValue('incomingServer.address');
   const portIncoming = getValue('incomingServer.port');

   const usernameOutgoing = getValue('outgoingServer.username');
   const passwordOutgoing = getValue('outgoingServer.password');
   const addressOutgoing = getValue('outgoingServer.address');
   const portOutgoing = getValue('outgoingServer.port');
   const authenticationOutgoing = getValue('outgoingServer.authentication');

   const {getValues} = useFormContext<EmailSettings>();

   return (
      <>
         <Panel name={t('Sending/Notification')}>
            <Grid container spacing={1} alignItems="center">
               <Offset xs={6} />
               <Grid item xs={6}>
                  <TestOutgoingEmailTransport
                     username={usernameOutgoing}
                     password={passwordOutgoing}
                     address={addressOutgoing}
                     port={portOutgoing}
                     authentication={authenticationOutgoing}
                     getValues={getValues}
                  />
               </Grid>
               <Grid item xs={6}>
                  <FormikTextField label={t('Email Server')} name="outgoingServer.address" />
               </Grid>
               <Grid item xs={6}>
                  <FormikTextField label={t('Port #')} name="outgoingServer.port" />
               </Grid>
               <Grid item xs={6}>
                  <FormikTextField label={t('Sender Address')} name="outgoingServer.fromField" />
               </Grid>
               <Grid item xs={6}>
                  <FormikDropdown
                     label={t('Encryption')}
                     name="outgoingServer.encryption"
                     options={emailEncryptionTypes}
                  />
               </Grid>
               <Grid item xs={6}>
                  <FormikCheckbox
                     label={t('Authentication Required')}
                     name="outgoingServer.authentication"
                  />
               </Grid>
               <Offset xs={6} />
               <Grid item xs={6}>
                  <FormikTextField
                     label={t('Account')}
                     name="outgoingServer.username"
                     disabled={!authentication}
                  />
               </Grid>
               <Grid item xs={6}>
                  <FormikPassphrase
                     label={t('Password')}
                     name="outgoingServer.password"
                     type={showOutboundServerPassword ? 'text' : 'password'}
                     disabled={!authentication}
                  />
               </Grid>
               <Offset xs={6} />
               {/* <ShowPasswordButton
                           show={showOutboundServerPassword}
                           onClick={toggleOutboundServerPassword}
                        /> */}
               <Grid item xs={6}>
                  <FormikNumberField
                     label={t('Delay Time (seconds)')}
                     name="outgoingServer.delay"
                  />
               </Grid>
               <Grid item xs={6}>
                  <FormikNumberField
                     label={t('Max Email Size (mbytes)')}
                     name="outgoingServer.maxSize"
                  />
               </Grid>
            </Grid>
         </Panel>
         <Panel name={t('Job Status Notification Subject')}>
            <FormikTextField label={t('Email Subject')} name="transSubjectTemplate" />
         </Panel>
         <Panel name={t('Receiving')}>
            <Grid container spacing={1} alignItems="center">
               <Grid item xs={6}>
                  <FormikDropdown
                     autoFocus
                     label={t('Server Type')}
                     name="incomingServer.typeName"
                     options={IncomingEmailType.options}
                  />
               </Grid>
               <Grid item xs={6}>
                  <TestIncomingEmailTransport
                     getValues={getValues}
                     username={usernameIncoming}
                     password={passwordIncoming}
                     address={addressIncoming}
                     port={portIncoming}
                  />
               </Grid>
               <Grid item xs={6}>
                  <FormikTextField label={t('Email Server')} name="incomingServer.address" />
               </Grid>
               <Grid item xs={6}>
                  <FormikTextField label={t('Port #')} name="incomingServer.port" />
               </Grid>
               <Grid item xs={6}>
                  <FormikTextField label={t('Account')} name="incomingServer.username" />
               </Grid>
               <Grid item xs={6}>
                  <FormikPassphrase
                     label={t('Password')}
                     name="incomingServer.password"
                     type={showInboundServerPassword ? 'text' : 'password'}
                  />
               </Grid>
               <Grid item xs={6}>
                  <FormikDropdown
                     label={t('Encryption')}
                     name="incomingServer.encryption"
                     options={emailEncryptionTypes}
                  />
               </Grid>
               {/* <ShowPasswordButton
                           show={showInboundServerPassword}
                           onClick={toggleInboundServerPassword}
                        /> */}
            </Grid>
         </Panel>
         <LastUpdatedLabel date={timestamp} author={lastUser} />
         <Box textAlign="center">
            <FormFooterButtons cancel={cancel} />
         </Box>
      </>
   );
};

const SettingsEmailFormWrapper = ({ok, cancel}: FormProps) => {
   const settings = useEmailSettings();

   const initial = React.useMemo(
      () => ({
         ...settings,
         outgoingServer: {
            ...settings.outgoingServer,
            delay: settings.outgoingServer?.delay / 1000,
         },
      }),
      [settings]
   );

   return (
      <AsyncContainer loading={isEmpty(settings)}>
         <Formik
            initialValues={emailSettingsSchema.cast(initial)}
            onSubmit={(values, callbacks) => {
               values.outgoingServer.delay *= 1000;
               return ok(castToSend(emailSettingsSchema, values), callbacks);
            }}
            validationSchema={emailSettingsSchema}
            enableReinitialize
         >
            <SettingsEmailForm cancel={cancel} />
         </Formik>
      </AsyncContainer>
   );
};

type DialogProps = {
   onClose: () => void;
};
export const SettingsEmailDialog = ({onClose}: DialogProps) => {
   const sendData = useSendData();
   return (
      <Dialog maxWidth="md" header={DialogTypes.SettingsEmailDialog.dialogName} close={onClose}>
         <SettingsEmailFormWrapper
            ok={(values, callbacks) => {
               sendData(DialogTypes.SettingsEmailDialog.id, values, callbacks);
               onClose();
            }}
            cancel={onClose}
         />
      </Dialog>
   );
};
export default React.memo(SettingsEmailDialog);
